/**
 * The account API (DSPRT-75): sign-in, favourites, search history and the
 * admin's user list. Same origin as the rest of /api, so the session cookie
 * (HttpOnly, scoped to /api) rides along on its own; nothing here ever sees
 * or stores the token.
 */
import { slugify } from "../router";

const NBA_API: string = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1/nba";
const API = NBA_API.replace(/\/nba\/?$/, "");
/** The API root, for the one link that's a plain navigation (the data download). */
export const API_BASE = API;

export type League = "nba" | "ufc" | "euroleague" | "gleague";
export type FavoriteKind = "team" | "player" | "fighter";

export interface User {
  id: number;
  email: string;
  displayName: string;
  role: "USER" | "ADMIN";
  createdAt: string;
  admin: boolean;
}

/** What's saved: `ref` is a team's abbreviation, an ESPN athlete id or a fighter's slug. */
export interface SavedItem {
  league: League;
  kind: FavoriteKind;
  ref: string;
  label: string;
  sub: string | null;
  imageUrl: string | null;
}

export interface Favorite extends SavedItem {
  id: number;
  createdAt: string;
  /** False when muted (DSPRT-651, V66): still a favorite, bringing no alerts. Absent from an older server. */
  alerts?: boolean;
}

export interface SearchEntry extends SavedItem {
  id: number;
  query: string;
  searchedAt: string;
}

export interface UserRow {
  id: number;
  email: string;
  displayName: string;
  role: "USER" | "ADMIN";
  createdAt: string;
  lastLoginAt: string | null;
  lastSeenAt: string | null;
  disabledAt: string | null;
  favorites: number;
  searches: number;
  sessions: number;
}

export interface AdminUsers {
  totals: { users: number; admins: number; disabled: number; newThisWeek: number; activeThisWeek: number };
  users: UserRow[];
}

/** A refusal with words for the reader: the server's own `error`, or a generic one. */
export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function call<T>(method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API}${path}`, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "Couldn't reach StatSummit. Check your connection and try again.");
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = data && typeof data.error === "string" ? data.error : "Something went wrong. Try again.";
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

/** Where a saved item opens: its own page on the site. */
export function hrefFor(item: Pick<SavedItem, "kind" | "ref" | "label"> & { league?: League }): string {
  // A EuroLeague club or player lives on its own desk (DSPRT-451).
  if (item.league === "euroleague") return item.kind === "team" ? `/euroleague/team/${item.ref}` : `/euroleague/player/${item.ref}`;
  // A G League club's page (DSPRT-474); its players are NBA players, saved as such.
  if (item.league === "gleague") return item.kind === "team" ? `/gleague/team/${item.ref}` : `/player/${slugify(item.label)}`;
  if (item.kind === "team") return `/team/${item.ref}`;
  if (item.kind === "fighter") return `/ufc/fighter/${item.ref}`;
  return `/player/${slugify(item.label)}`;
}

/**
 * The sign-in page, with a way back to where you were. Only a path on this
 * site is accepted as `next` (see safeNext), so the link can't be turned into
 * a redirect to somewhere else.
 */
export function signInHref(next: string = location.pathname + location.search): string {
  return next && next !== "/" ? `/signin?next=${encodeURIComponent(next)}` : "/signin";
}

export function safeNext(raw: string | null): string | null {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return null;
  if (/^\/(signin|signup)(\/|\?|$)/.test(raw)) return null;
  return raw;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

/** "just now", "5 min ago", "3 h ago", "yesterday", then a date: how long since a search, or since someone was seen. */
export function timeAgo(iso: string, now: number = Date.now()): string {
  const mins = Math.round((now - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  if (hours < 48) return "yesterday";
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString("en-US", sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}

/* ------------------------------------------------------------------ *
 * Second round (DSPRT-76 to 83)
 * ------------------------------------------------------------------ */

export interface AuthConfig {
  passwordReset: boolean;
  googleClientId: string | null;
}

export interface SessionView {
  createdAt: string;
  lastSeenAt: string;
  userAgent: string | null;
  current: boolean;
  /** "ios" for a session the app opened (DSPRT-644); absent for a browser. */
  client?: string | null;
}

export interface AlertSettings {
  tipoff: boolean;
  /** Within five with under five minutes left (DSPRT-323). */
  close: boolean;
  /** A followed player reaches 40 points or a triple-double (DSPRT-350); absent from a server that doesn't have it yet. */
  big?: boolean;
  final: boolean;
  fights: boolean;
  startingSoon: boolean;
  email: boolean;
  trades: boolean;
  injuries: boolean;
  /** Leagues the account hears nothing from (V66): nba, gleague, euroleague, ufc. Left out of a PUT, what's stored stays. */
  leaguesOff?: string[];
}

export interface AlertsInfo {
  settings: AlertSettings;
  devices: number;
  vapidKey: string;
  emailAvailable: boolean;
}

export interface HealthCard {
  key: string;
  name: string;
  status: "OK" | "STALE" | "DOWN";
  lines: string[];
}

export interface JobStatus {
  key: string;
  label: string;
  description: string;
  cadence: string;
  state: "RUNNING" | "OK" | "FAILED" | "IDLE";
  startedAt: string | null;
  finishedAt: string | null;
  millis: number | null;
  message: string | null;
  by: string | null;
  /** What the job's data looks like whether or not it has run: "Stored for 1,204 of 1,688 players". */
  note?: string | null;
  /** A running job that can be asked to stop (Player totals). */
  stoppable?: boolean;
}

export interface RosterChange {
  kind: "ADDED" | "CUT" | "TRADED";
  id: string;
  name: string;
  from: string | null;
  to: string | null;
  at: string;
}

export interface RosterScan {
  at: string | null;
  teams: number;
  players: number;
  baseline: boolean;
  latest: RosterChange[];
  log: RosterChange[];
  unread: string[];
}

export interface HealthReport {
  checkedAt: string;
  needAttention: number;
  cards: HealthCard[];
  jobs: JobStatus[];
}

export interface TopItem {
  league: League;
  kind: FavoriteKind;
  ref: string;
  label: string;
  imageUrl: string | null;
  people: number;
}

export interface Stats {
  tiles: { users: number; favorites: number; searchesOpened: number; alertDevices: number };
  signups: Array<{ date: string; count: number }>;
  teams: TopItem[];
  players: TopItem[];
  fighters: TopItem[];
  opened: TopItem[];
  misses: Array<{ league: League; query: string; hits: number; lastAt: string }>;
}

export interface ActivityEvent {
  id: number;
  at: string;
  kind: string;
  group: "signin" | "account" | "admin";
  actorId: number | null;
  actorLabel: string | null;
  targetId: number | null;
  targetLabel: string | null;
  ip: string | null;
  userAgent: string | null;
  detail: string | null;
}

export interface Announcement {
  message: string;
  tone: "info" | "warning";
  linkUrl: string | null;
  linkText: string | null;
  endsAt: string | null;
  active: boolean;
  publishedAt: string | null;
  updatedAt: string;
  live: boolean;
}

/** One "starting soon" alert (DSPRT-116): any number per league, edited from /admin/alerts. */
export interface AlertRule {
  id: number;
  league: "nba" | "ufc";
  leadMinutes: number;
  title: string;
  body: string;
  updatedAt: string;
}

/** "Chrome on macOS", from a user-agent string; good enough to tell your devices apart. */
export function describeBrowser(ua: string | null | undefined): string {
  if (!ua) return "Unknown browser";
  // The iOS app (DSPRT-644, C-07) names itself first: "StatSummit/1.0 (iPhone; iOS 26.0)".
  if (/^StatSummit\//.test(ua)) return /iPad/.test(ua) ? "StatSummit app on iPad" : "StatSummit app on iPhone";
  const browser =
    /Edg\//.test(ua) ? "Edge"
    : /OPR\/|Opera/.test(ua) ? "Opera"
    : /SamsungBrowser/.test(ua) ? "Samsung Internet"
    : /Firefox\/|FxiOS/.test(ua) ? "Firefox"
    : /Chrome\/|CriOS/.test(ua) ? "Chrome"
    : /Safari\//.test(ua) ? "Safari"
    // Not a browser at all (curl, a script): its own first word says what it is.
    : ua.split(/[/\s]/)[0] || "Unknown client";
  const os =
    /iPhone/.test(ua) ? "iPhone"
    : /iPad/.test(ua) ? "iPad"
    : /Android/.test(ua) ? "Android"
    : /Mac OS X|Macintosh/.test(ua) ? "macOS"
    : /Windows/.test(ua) ? "Windows"
    : /CrOS/.test(ua) ? "ChromeOS"
    : /Linux/.test(ua) ? "Linux"
    : null;
  return os ? `${browser} on ${os}` : browser;
}
